// AvailablePagesTab.js - Manage available pages in the system
import {
  useState,
  useEffect,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  Switch,
  Alert,
  DataGrid,
  IconButton,
  EditIcon,
  DeleteIcon,
  authFetch,
  authFetchRequest
} from './SettingsCommon';

import AvailablePageDialog from './AvailablePageDialog';

const AvailablePagesTab = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);

  // Fetch pages from API (include inactive pages for management)
  const fetchPages = () => {
    setLoading(true);
    setError(null);
    authFetch('/user-roles/available-pages?all=true')
      .then(data => { setPages(data); setLoading(false); })
      .catch(err => {
        console.error('Error fetching pages:', err);
        setError('Failed to load pages. Please try again.');
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchPages();
  }, []);

  // CRUD handlers
  const handleAdd = () => {
    setEditData(null);
    setOpenDialog(true);
  };

  const handleEdit = (page) => {
    setEditData(page);
    setOpenDialog(true);
  };

  const handleSave = async (formData) => {
    try {
      setError(null);
      let updatedPage;
      const pageId = editData?.page_id || editData?.id;
      if (editData && pageId) {
        // Update existing page
        updatedPage = await authFetchRequest(`/user-roles/available-pages/${pageId}`, formData, 'PUT');
        setPages(prevPages => prevPages.map(page =>
          (page.page_id || page.id) === pageId ? updatedPage : page
        ));
      } else {
        // Create new page
        updatedPage = await authFetchRequest('/user-roles/available-pages', formData, 'POST');
        setPages(prevPages => [...prevPages, updatedPage]);
      }
      setOpenDialog(false);
      setEditData(null);
    } catch (err) {
      console.error('Error saving page:', err);
      setError(`Failed to save page: ${err.message}`);
      throw err;
    }
  };

  const handleDelete = async (page) => {
    const pageId = page.page_id || page.id;
    if (window.confirm(`Are you sure you want to delete the page "${page.label}"? This will also remove it from all roles.`)) {
      try {
        setError(null);
        await authFetchRequest(`/user-roles/available-pages/${pageId}`, null, 'DELETE');
        setPages(prevPages => prevPages.filter(p => (p.page_id || p.id) !== pageId));
      } catch (err) {
        console.error('Error deleting page:', err);
        setError(`Failed to delete page: ${err.message}`);
      }
    }
  };

  const handleToggleActive = async (page) => {
    const pageId = page.page_id || page.id;
    try {
      setError(null);
      const updatedPage = await authFetchRequest(`/user-roles/available-pages/${pageId}`, {
        ...page,
        active: !page.active
      }, 'PUT');
      setPages(prevPages => prevPages.map(p =>
        (p.page_id || p.id) === pageId ? updatedPage : p
      ));
    } catch (err) {
      console.error('Error toggling page active status:', err);
      setError(`Failed to update page: ${err.message}`);
    }
  };

  const columns = [
    {
      field: 'page_id',
      headerName: 'Page ID',
      width: 120,
      valueGetter: (_, row) => row.page_id || row.id
    },
    { field: 'label', headerName: 'Label', width: 150 },
    { field: 'path', headerName: 'Path', width: 150 },
    { field: 'sort_order', headerName: 'Order', width: 80 },
    {
      field: 'active',
      headerName: 'Active',
      width: 100,
      renderCell: (params) => (
        <Switch
          checked={params.value}
          onChange={() => handleToggleActive(params.row)}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleEdit(params.row); }}
            title="Edit Page"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleDelete(params.row); }}
            title="Delete Page"
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      )
    }
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Available Pages Management</Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" onClick={handleAdd}>
          Add Page
        </Button>
        <Button variant="outlined" onClick={fetchPages} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
        <Typography variant="body2">
          Manage the list of pages available in the system. These pages can be assigned to user roles.
          Note: Adding a page here doesn't create the route - routes must be defined in App.js.
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGrid
          autoHeight
          rowHeight={45}
          rows={pages}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          getRowId={(row) => row.page_id || row.id}
          sx={{
            '& .MuiDataGrid-row': { cursor: 'pointer' }
          }}
        />
      )}

      {/* Page Create/Edit Dialog */}
      <AvailablePageDialog
        open={openDialog}
        data={editData}
        onClose={() => { setOpenDialog(false); setEditData(null); }}
        onSave={handleSave}
      />
    </Box>
  );
};

export default AvailablePagesTab;
